/**
 * FERP Portal - Unified Navigation Context
 *
 * DRR-313: Consolidates navigation queries to reduce DDB load.
 * Fetches once on mount, provides to all consumers via context.
 *
 * ATOMIC STATE PATTERN (DRR-313 Amendment):
 * Uses a "committed state" pattern to eliminate race conditions during user/tenant switch.
 * - Queries run independently and may complete at different times
 * - Context only exposes "committed" state where ALL data is consistent
 * - Partial updates are buffered until complete
 * - Previous committed state shown during transitions (no flicker)
 *
 * Replaces duplicate calls to:
 * - getAllowedNavigation (was called 3+ times)
 * - getSettingNavigation (was called 2+ times)
 * - getTenantSubgraphConfig (was called 2+ times)
 *
 * FERP Principles:
 * - Single fetch per session (cache-first)
 * - Fail-closed: empty data while loading
 * - Backend authority: all data from GraphQL
 * - Atomic transitions: no partial state exposed
 */

import React, { createContext, useContext, useMemo, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  useGetAllowedNavigationQuery,
  useGetSettingNavigationQuery,
  useGetTenantSubgraphConfigQuery,
  useMyViewLayerQuery,
  ViewLayer as GqlViewLayer,
  type GetAllowedNavigationQuery,
  type GetSettingNavigationQuery,
  type GetTenantSubgraphConfigQuery,
} from '@/generated/graphql';
import { useAuth } from '@/auth/AuthContext';

// =============================================================================
// Types
// =============================================================================

interface NavigationContextValue {
  // Loading state - true only on initial load, NOT during transitions
  loading: boolean;

  // True during user/tenant switch when refetching
  transitioning: boolean;

  // Allowed navigation (scopes, nav items, routes)
  allowedNavigation: GetAllowedNavigationQuery['allowedNavigation'] | null;
  userScopes: Set<string>;

  // Setting navigation (user menu, industry config)
  settingBundle: GetSettingNavigationQuery['settingBundle'] | null;

  // Tenant subgraph config (routes, entity types, etc.)
  subgraphConfig: GetTenantSubgraphConfigQuery['getTenantSubgraphConfig'] | null;

  // Convenience methods
  hasScope: (scope: string) => boolean;
  hasAnyScope: (scopes: string[]) => boolean;
}

const defaultValue: NavigationContextValue = {
  loading: true,
  transitioning: false,
  allowedNavigation: null,
  userScopes: new Set(),
  settingBundle: null,
  subgraphConfig: null,
  hasScope: () => false,
  hasAnyScope: () => false,
};

const NavigationContext = createContext<NavigationContextValue>(defaultValue);

// =============================================================================
// Committed State Type
// =============================================================================

interface CommittedNavState {
  allowedNavigation: GetAllowedNavigationQuery['allowedNavigation'] | null;
  settingBundle: GetSettingNavigationQuery['settingBundle'] | null;
  subgraphConfig: GetTenantSubgraphConfigQuery['getTenantSubgraphConfig'] | null;
  userScopes: Set<string>;
  // Track which user/tenant this state belongs to (for invalidation)
  forActorId: string;
  forTenantId: string;
}

// =============================================================================
// Provider
// =============================================================================

interface NavigationProviderProps {
  children: ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  const { tenant, isAuthenticated, setViewLayer } = useAuth();
  const tenantId = tenant?.id ?? '';

  // Track current actor for detecting user switches
  const currentActorId = globalThis.window === undefined
    ? ''
    : globalThis.localStorage.getItem('dev_actor_auth_id') ?? '';

  // Skip all queries if not authenticated
  const skipAuth = !isAuthenticated;
  const skipTenant = !tenantId;

  // DRR-313: Fetch view layer FIRST, then use it for navigation query
  // This eliminates the cascade: ViewLayerSync → AuthContext → NavigationContext re-query
  //
  // cache-and-network (NOT cache-first): myViewLayer has no query variables, so
  // Apollo caches a single value across all actors. With cache-first, switching
  // from a user-layer actor to an admin/architect actor (e.g. CEO) returns the
  // STALE cached 'USER' and never refetches — locking the new actor to the
  // wrong layer. cache-and-network shows the cached value instantly but always
  // refetches, so the backend (authoritative) view layer wins on actor switch.
  const { data: viewLayerData, loading: viewLayerLoading } = useMyViewLayerQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    skip: skipAuth,
  });

  // Sync view layer to AuthContext (for other consumers like DevPanel)
  const backendLayer = viewLayerData?.myViewLayer?.toLowerCase() as 'user' | 'admin' | 'architect' | undefined;
  useEffect(() => {
    if (!backendLayer) return;
    // Don't override DevPanel manual selection
    const isDevMode = ((window as unknown as { __ENV__?: { ENABLE_DEV_MODE?: string } }).__ENV__?.ENABLE_DEV_MODE) === 'true';
    const devOverride = sessionStorage.getItem('portal:viewLayer');
    if (isDevMode && devOverride) return;
    setViewLayer(backendLayer);
  }, [backendLayer, setViewLayer]);

  // Use backend layer directly (not from AuthContext to avoid cascade)
  const gqlLayer = (backendLayer?.toUpperCase() ?? 'USER') as GqlViewLayer;

  // DRR-313: Always use cache-and-network for nav queries.
  // - Shows cached data instantly (fast first paint)
  // - Refetches in background (picks up role/scope changes)
  // - Payload is tiny (~2KB), no perf impact
  // This ensures tenant admin role changes take effect on next navigation.
  const navFetchPolicy = 'cache-and-network' as const;

  // Single fetch for allowed navigation - waits for view layer
  const { data: allowedNavData, loading: allowedLoading } = useGetAllowedNavigationQuery({
    variables: { tenantId, layer: gqlLayer },
    skip: skipTenant || viewLayerLoading,
    fetchPolicy: navFetchPolicy,
  });

  // Single fetch for setting navigation
  const { data: settingNavData, loading: settingLoading } = useGetSettingNavigationQuery({
    skip: skipTenant,
    fetchPolicy: navFetchPolicy,
  });

  // Single fetch for tenant subgraph config
  const { data: subgraphConfigData, loading: configLoading } = useGetTenantSubgraphConfigQuery({
    variables: { tenantId },
    skip: skipTenant,
    fetchPolicy: navFetchPolicy,
  });

  // ==========================================================================
  // ATOMIC STATE PATTERN: Committed state that only updates when ALL data ready
  // ==========================================================================

  const [committedState, setCommittedState] = useState<CommittedNavState | null>(null);
  const pendingCommitRef = useRef<{
    allowedNavigation: typeof allowedNavData;
    settingBundle: typeof settingNavData;
    subgraphConfig: typeof subgraphConfigData;
    forActorId: string;
    forTenantId: string;
  } | null>(null);

  // Detect if we're in a transition (user/tenant changed but data not yet committed)
  const isTransitioning = useMemo(() => {
    if (!committedState) return false;
    return committedState.forActorId !== currentActorId ||
           committedState.forTenantId !== tenantId;
  }, [committedState, currentActorId, tenantId]);

  // Check if all queries have returned fresh data for current user/tenant
  const allQueriesReady = !viewLayerLoading && !allowedLoading && !settingLoading && !configLoading;
  const hasAllData = allowedNavData?.allowedNavigation && settingNavData?.settingBundle;

  // Commit new state only when ALL data is ready and consistent
  useEffect(() => {
    if (!allQueriesReady || !hasAllData) {
      // Still loading - buffer the partial data
      pendingCommitRef.current = {
        allowedNavigation: allowedNavData,
        settingBundle: settingNavData,
        subgraphConfig: subgraphConfigData,
        forActorId: currentActorId,
        forTenantId: tenantId,
      };
      return;
    }

    // All data ready - commit atomically
    // DRR-335: Use settingBundle.userScopes as fallback when allowedNavigation is empty
    // This happens when SubgraphRegistry isn't seeded (local dev / new tenant).
    const allowedScopes = allowedNavData?.allowedNavigation?.userScopes ?? [];
    const settingScopes = (settingNavData?.settingBundle as { userScopes?: string[] })?.userScopes ?? [];
    const newScopes = new Set(allowedScopes.length > 0 ? allowedScopes : settingScopes);

    setCommittedState({
      allowedNavigation: allowedNavData?.allowedNavigation ?? null,
      settingBundle: settingNavData?.settingBundle ?? null,
      subgraphConfig: subgraphConfigData?.getTenantSubgraphConfig ?? null,
      userScopes: newScopes,
      forActorId: currentActorId,
      forTenantId: tenantId,
    });

    pendingCommitRef.current = null;
  }, [
    allQueriesReady,
    hasAllData,
    allowedNavData,
    settingNavData,
    subgraphConfigData,
    currentActorId,
    tenantId
  ]);

  // Use committed state, falling back to current query data for initial load
  const effectiveAllowedNav = committedState?.allowedNavigation ?? allowedNavData?.allowedNavigation ?? null;
  const effectiveSettingBundle = committedState?.settingBundle ?? settingNavData?.settingBundle ?? null;
  const effectiveSubgraphConfig = committedState?.subgraphConfig ?? subgraphConfigData?.getTenantSubgraphConfig ?? null;
  // DRR-335: Use settingBundle.userScopes as fallback when allowedNavigation is empty
  const fallbackScopes = (() => {
    const allowedScopes = allowedNavData?.allowedNavigation?.userScopes ?? [];
    if (allowedScopes.length > 0) return allowedScopes;
    return (settingNavData?.settingBundle as { userScopes?: string[] })?.userScopes ?? [];
  })();
  const effectiveScopes = committedState?.userScopes ?? new Set(fallbackScopes);

  // Scope check helpers - use effective (committed) scopes
  const hasScope = useMemo(() => {
    return (scope: string) => {
      if (effectiveScopes.has('*') || effectiveScopes.has('*:*')) return true;
      return effectiveScopes.has(scope);
    };
  }, [effectiveScopes]);

  const hasAnyScope = useMemo(() => {
    return (scopes: string[]) => {
      if (effectiveScopes.has('*') || effectiveScopes.has('*:*')) return true;
      return scopes.some((s) => effectiveScopes.has(s));
    };
  }, [effectiveScopes]);

  // Loading = true only on initial load (no committed state yet)
  const isInitialLoad = !committedState && (viewLayerLoading || allowedLoading || settingLoading || configLoading);

  const value = useMemo<NavigationContextValue>(
    () => ({
      loading: isInitialLoad,
      transitioning: isTransitioning,
      allowedNavigation: effectiveAllowedNav,
      userScopes: effectiveScopes,
      settingBundle: effectiveSettingBundle,
      subgraphConfig: effectiveSubgraphConfig,
      hasScope,
      hasAnyScope,
    }),
    [
      isInitialLoad,
      isTransitioning,
      effectiveAllowedNav,
      effectiveScopes,
      effectiveSettingBundle,
      effectiveSubgraphConfig,
      hasScope,
      hasAnyScope,
    ]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Access consolidated navigation data.
 * Replaces direct useGetAllowedNavigationQuery, useGetSettingNavigationQuery,
 * and useGetTenantSubgraphConfigQuery calls.
 *
 * Returns committed (consistent) state - never partial updates.
 */
export function useNavigation(): NavigationContextValue {
  return useContext(NavigationContext);
}

/**
 * Get user scopes for authorization checks.
 * Replaces useGetAllowedNavigationQuery just for scopes.
 */
export function useUserScopes(): { scopes: Set<string>; loading: boolean } {
  const { userScopes, loading } = useContext(NavigationContext);
  return { scopes: userScopes, loading };
}

/**
 * Get setting bundle for user menu / industry config.
 * Replaces useGetSettingNavigationQuery.
 */
export function useSettingBundle() {
  const { settingBundle, loading } = useContext(NavigationContext);
  return { settingBundle, loading };
}

/**
 * Get tenant subgraph config for routes/entity types.
 * Replaces useGetTenantSubgraphConfigQuery.
 */
export function useSubgraphConfig() {
  const { subgraphConfig, loading } = useContext(NavigationContext);
  return { subgraphConfig, loading };
}
